// topo do arquivo
import { useEffect, useRef, useState } from "react";
import { Toast } from "primereact/toast";
import "./ProjectTeamModal.css";
import { assignAgentToProjectRequest, removeAgentFromProjectRequest, updateUserRoleForProject } from "../../services/wisdomServices/projectService";
import WisdomDropdown from '../dropdown/WisdomDropdown'
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { FilterMatchMode } from 'primereact/api';
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import { InputText } from "primereact/inputtext";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { AutoComplete } from "primereact/autocomplete";
import { requestGetAllUser } from "../../services/wisdomServices/userService";
import { Can, PERMISSIONS } from "../../security";

/**
 * @function ProjectRoleCell
 * @param {Object} props - component props
 * @param {Object} props.row - table row data representing a project member
 * @param {string|number} props.row.id - user ID
 * @param {string|number} props.row.roleId - current role ID assigned to the user
 * @param {Array<Object>} props.projectRoles - available project roles
 * @param {Function} props.updateProjectRole - callback to update user's project role
 * @returns {JSX.Element} - role selection cell
 */
const ProjectRoleCell = ({ row, projectRoles, updateProjectRole }) => {
  const selectedRole = projectRoles.find((r) => r.value === row.roleId);
  /**
   * @function handleChange
   * @param {Object} selectedOption - selected role option
   * @param {string|number} selectedOption.value - role ID
   * @description - handles role selection change
   */
  const handleChange = (selectedOption) => {
    updateProjectRole(row.id, selectedOption.value);
  };

  return (
    <WisdomDropdown 
      placeholder="Select a role"
      value={selectedRole.value}
      onChange={handleChange}
      options={projectRoles}
      width="250px"
      optionalLabel="label"
      optionalValue="value"
    />
  );
};

/**
 * @function ProjectTeamModal
 * @param {Object} props - component props
 * @param {Object} props.teamMembers - project data including current agents
 * @param {Array<Object>} props.teamMembers.agents - list of project members
 * @param {string|number} props.teamMembers.id - project ID
 * @param {string} props.teamMembers.name - project name
 * @param {Array<Object>} props.users - list of all available users
 * @param {Array<Object>} props.projectRoles - available project roles
 * @param {Function} props.updateTeamMembers - callback to refresh team members after changes
 * @param {Function} props.onClose - callback to close the modal
 * @returns {JSX.Element} - project team management modal
 */
export function ProjectTeamModal(props) {
  const toast = useRef(null);
  const debounceRef = useRef(null);
  const [user, setUser] = useState("");
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedProjectRole, setSelectedProjectRole] = useState("");
  const [filters, setFilters] = useState({
       global: {value:null, matchMode: FilterMatchMode.CONTAINS},
       agentname: {value:null, matchMode: FilterMatchMode.CONTAINS}
   })
   const [globalFilterValue, setGlobalFilterValue] = useState('');
   const globalFilters = ['agentname']
   const onGlobalFilterChange = (e) => {
       const value = e.target.value;
       let _filters = { ...filters };
 
       _filters['global'].value = value;
 
       setFilters(_filters);
       setGlobalFilterValue(value);
   };

  /**
   * @function showError
   * @param {string} text - message content
   * @description - displays an error toast message
   */
  const showError = (text) => {
    toast.current.show({
      severity: "error",
      summary: "Error",
      detail: text,
      life: 3000,
    });
  };

  /**
   * @async
   * @function addMember
   * @description - adds a selected user to the project team with the selected role
   */
  const addMember = async () => {
    if (!selectedUser || !selectedProjectRole) return;

    const userAlreadyInTeam = props.teamMembers.agents.some(
      (agent) => agent.id === selectedUser.id
    );

    if (userAlreadyInTeam) {
      showError("User is already in the team");
      return;
    }


    const keys = {
      projectRoleId: selectedProjectRole.value,
    };
    try {
      await assignAgentToProjectRequest(props.teamMembers.id, selectedUser.id, keys);
      props.updateTeamMembers(props.teamMembers);
      
    } catch (error) {
      console.error(error)
    }
  };

  /**
   * @async
   * @function removeMember
   * @param {Object} data - agent data
   * @description - removes a user from the project team
   */
  const removeMember = async (data) => {
    try {
      await removeAgentFromProjectRequest(props.teamMembers.id, data.id);
      props.updateTeamMembers(props.teamMembers);
      
    } catch (error) {
      console.error(error)
    }
  };

  /**
   * @async
   * @function updatedProjects
   * @param {string|number} userID - user ID
   * @param {string|number} roleId - role ID
   * @description - updates the role of a user within the project
   */
  const updateProjectRole = async (userID, roleId) => {
    try {
      await updateUserRoleForProject( props.teamMembers.id, userID, roleId)
    } catch (error) {
      console.error("Failed to update role:", error);
      
    } finally{
      props.updateTeamMembers(props.teamMembers);
      
    }
  };

  const projectRoleTemplate = (rowData) => {
    return(
      <ProjectRoleCell
        row={rowData}
        projectRoles={props.projectRoles}
        updateProjectRole={updateProjectRole}
      />

    )
  }

  const removeAgentTemplate = (rowData) => {
    return(
      <Can permission={PERMISSIONS.projectsAgentsRemove_post}>
        <Button
          label="Delete Member"
          severity="warning"
          className="team-members-remove-agent-btn"
          onClick={() => removeMember(rowData)}
        />
      </Can>
    )
  }
  const columns = [
    {
      field: "agentname",
      header: "Name",
    },
    {
      header: "Project Role",
      body: projectRoleTemplate,
    },
    {
      header: "Remove Agent",
      body: removeAgentTemplate,
    },
  ];

  /**
   * @function handleSelectedProjectRole
   * @param {Object} projectRole - selected role
   * @description - handles project role selection
   */
  const handleSelectedProjectRole = (projectRole) => {
    setSelectedProjectRole(projectRole);
  };

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        props.onClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [props.onClose]);

  const closeModal = () => {
    props.onClose();
  };

  const dialogHeader = (
    <div>
      <span>Project Team Members - {props.teamMembers.name}</span>
    </div>
  )

  const dialogFooter = (
    <div>
      <Button label="Close" icon="pi pi-times" autoFocus onClick={closeModal}/>
    </div>
  )

  const header = (
    <div className="project-team-modal-header">
      <div>
        <IconField iconPosition="left">
            <InputIcon className="pi pi-search" />
            <InputText value={globalFilterValue} onChange={onGlobalFilterChange} placeholder="Keyword Search" />
        </IconField>
      </div>
    </div>
  )

  const searchTeamMember = async(event) => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(async () => {
      try {
        const response = await requestGetAllUser(event.query, 1, 100)
        const userList = response.data.map((user) => ({
          id: user.id,
          name: user.fullName,
          email: user.username,
        }));
        setUsers(userList);
      } catch (error) {
        console.error(error)
      }
    }, 1000);
  }

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      }
    };
  }, []);

  return (
    <Dialog
      dismissableMask
      className="project-team-modal-container"
      visible={props.open}
      onHide={closeModal}
      header={dialogHeader}
      footer={dialogFooter}
      modal
    >
      <div>
        <Toast ref={toast} position="top-center" />
        <div className="project-team-modal-box">
          <AutoComplete
            field="name"
            value={user}
            placeholder="Search user..."
            suggestions={users}
            completeMethod={searchTeamMember}
            onChange={(e) => setUser(e.value)}
            onSelect={(e) => setSelectedUser(e.value)}
          />
          <WisdomDropdown 
            placeholder="Select a role"
            name="projectRole"
            value={selectedProjectRole.value}
            onChange={handleSelectedProjectRole}
            options={props.projectRoles}
          />
          <div>
            <Can permission={PERMISSIONS.projectsByProjectidAgentsByAgentid_post}>
              <Button label="Add" icon='pi pi-plus-circle' onClick={addMember} title="Add member to the project"/>
            </Can>
          </div>
        </div>
        <div>
          <DataTable
            header={header}
            value={props.teamMembers.agents}
            columns={columns}
            filters={filters}
            globalFilterFields={globalFilters}
            paginator
            rows={5}
          >
            {columns.map((column, i) => {
              return(
                <Column 
                    key={column.field}
                    field={column.field} 
                    header={column.header} 
                    body={column?.body}
                    bodyStyle={column?.bodyStyle}
                    headerStyle={column?.headerStyle}
                />
              )
            })}
          </DataTable>
        </div>
      </div>
    </Dialog>
  );
}
